// @section 1 TimeValue route — direct-time allocation vs target, 4-level drill-through
// Data: internal_ops.time_value.* (via getTimeValueData — real endpoint when
// deployed, real-data fixture until then). All drill/filter/search is client-side
// over the loaded payload, so date-range + global search reshape every view.

import { useEffect, useMemo, useState } from 'react';
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
  Legend, LineChart, Line,
} from 'recharts';
import {
  getTimeValueData, type TvPayload, type TvSession, type TvInsight,
} from '../api';
import { toast } from '../components/Toast';

// @section 2 Palette helpers (V14 tokens)
const CF_COLOR: Record<string, string> = {
  Direct: '#14b8a6', Leverage: '#10b981', 'Cost-avoided': '#f59e0b', Overhead: '#6b7a90',
};
const SEV_RING: Record<string, string> = { ok: 'border-ok/40', watch: 'border-watch/40', stall: 'border-stall/40' };
const SEV_TX: Record<string, string> = { ok: 'text-ok', watch: 'text-watch', stall: 'text-stall' };
const LINE_PAL = ['#14b8a6','#10b981','#f59e0b','#f43f5e','#2dd4bf','#6b7a90','#818cf8','#e879f9','#fb923c','#a3e635'];

type View = 'overview' | 'bucket' | 'subcat' | 'session';

// @section 3 Main
export default function TimeValue() {
  const [payload, setPayload] = useState<TvPayload | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [view, setView] = useState<View>('overview');
  const [bucket, setBucket] = useState<string | null>(null);
  const [dim, setDim] = useState<'functionalCategory' | 'bundle' | null>(null);
  const [dimVal, setDimVal] = useState<string | null>(null);
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [search, setSearch] = useState('');

  useEffect(() => {
    getTimeValueData().then((p) => {
      setPayload(p);
      const days = p.sessions.map((s) => s.firstSeenAt.slice(0, 10)).sort();
      if (days.length) { setFrom(days[0]); setTo(days[days.length - 1]); }
    }).catch((e) => setErr(String(e)));
  }, []);

  // @subsection 3.1 Client-side filter (date + global search) → reshapes everything
  const sessions = useMemo(() => {
    if (!payload) return [];
    const q = search.trim().toLowerCase();
    return payload.sessions.filter((s) => {
      const d = s.firstSeenAt.slice(0, 10);
      if (from && d < from) return false;
      if (to && d > to) return false;
      if (!q) return true;
      return (
        s.sessionId.toLowerCase().includes(q) ||
        s.prompt.toLowerCase().includes(q) ||
        s.bucket.toLowerCase().includes(q) ||
        s.decisionReason.toLowerCase().includes(q) ||
        s.functionalCategory.toLowerCase().includes(q) ||
        s.bundle.toLowerCase().includes(q) ||
        s.tier.toLowerCase().includes(q) ||
        s.project.toLowerCase().includes(q) ||
        s.tools.toLowerCase().includes(q)
      );
    });
  }, [payload, from, to, search]);

  // @subsection 3.2 Recompute allocation from filtered sessions (targets stay fixed)
  const allocation = useMemo(() => {
    if (!payload) return [];
    const totalDirect = sessions.reduce((a, s) => a + s.directMinutes, 0) || 1;
    return payload.allocation.map((a) => {
      const rows = sessions.filter((s) => s.bucket === a.bucket);
      const directMinutes = rows.reduce((x, s) => x + s.directMinutes, 0);
      const actualPct = +(100 * directMinutes / totalDirect).toFixed(2);
      return {
        ...a, directMinutes,
        passiveMinutes: rows.reduce((x, s) => x + s.passiveMinutes, 0),
        costUsd: rows.reduce((x, s) => x + s.costUsd, 0),
        sessionCount: rows.length, actualPct,
        deltaPp: +(actualPct - (a.targetPct ?? 0)).toFixed(2),
      };
    });
  }, [payload, sessions]);

  const trend = useMemo(() => {
    if (!payload) return [];
    return payload.trend.filter((t) => (!from || t.day >= from) && (!to || t.day <= to));
  }, [payload, from, to]);

  if (err) return <div className="p-6 text-stall text-sm">Failed to load time-value data: {err}</div>;
  if (!payload) return <div className="p-6 text-muted text-sm animate-pulse">Loading time-value allocation…</div>;

  const go = (v: View) => setView(v);
  const drillBucket = (b: string) => { setBucket(b); setDim(null); setDimVal(null); go('bucket'); };
  const drillSubcat = (b: string, d: 'functionalCategory' | 'bundle', v: string) => {
    setBucket(b); setDim(d); setDimVal(v); go('subcat');
  };
  const drillSession = (id: string) => { setSessionId(id); go('session'); };

  return (
    // Phone: own the scroll + add gutters (md: keeps the flush desktop layout).
    <div className="text-paper-100 h-full overflow-y-auto px-4 py-5 md:px-0 md:py-0">
      {/* @subsection 3.3 Header + filters */}
      <div className="flex items-center justify-between flex-wrap gap-3 mb-2">
        <h1 className="text-xl font-bold text-paper-50 m-0">Time-Value Allocation</h1>
        <div className="flex items-center gap-2 text-xs flex-wrap">
          <span className={`px-2 py-0.5 rounded ${payload.source === 'fixture' ? 'bg-watch/20 text-watch' : 'bg-ok/20 text-ok'}`}>
            {payload.source === 'fixture' ? 'fixture (backend not deployed)' : 'live endpoint'}
          </span>
          <input type="date" value={from} onChange={(e) => setFrom(e.target.value)}
            className="bg-ink-900 border border-ink-700 rounded px-2 py-1 text-paper-100" />
          <span className="text-muted">→</span>
          <input type="date" value={to} onChange={(e) => setTo(e.target.value)}
            className="bg-ink-900 border border-ink-700 rounded px-2 py-1 text-paper-100" />
          <input placeholder="Search all sessions…" value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="bg-ink-900 border border-ink-700 rounded px-3 py-1 text-paper-100 w-56" />
        </div>
      </div>
      <div className="text-xs text-muted mb-4">
        {sessions.length} sessions · {Math.round(sessions.reduce((a, s) => a + s.directMinutes, 0) / 60)}h direct ·
        generated {payload.generatedAt.slice(0, 16).replace('T', ' ')} UTC
      </div>

      {/* @subsection 3.4 Breadcrumb */}
      <div className="text-xs text-muted mb-5">
        <a className="text-accent-400 cursor-pointer hover:underline" onClick={() => go('overview')}>Overview</a>
        {bucket && view !== 'overview' && <>
          {' › '}<a className="text-accent-400 cursor-pointer hover:underline" onClick={() => drillBucket(bucket)}>{bucket}</a>
        </>}
        {dimVal && (view === 'subcat' || view === 'session') && <>
          {' › '}<a className="text-accent-400 cursor-pointer hover:underline"
            onClick={() => drillSubcat(bucket!, dim!, dimVal)}>
            {dim === 'bundle' ? 'zone: ' : 'subcat: '}{dimVal}</a>
        </>}
        {view === 'session' && <> {' › '}<span>session</span></>}
      </div>

      {view === 'overview' && (
        <Overview payload={payload} allocation={allocation} trend={trend}
          onBucket={drillBucket} user={payload.user} />
      )}
      {view === 'bucket' && bucket && (
        <BucketDetail bucket={bucket} allocation={allocation} sessions={sessions}
          onSubcat={drillSubcat} />
      )}
      {view === 'subcat' && bucket && dim && dimVal && (
        <SessionTable
          rows={sessions.filter((s) => s.bucket === bucket && (s as any)[dim] === dimVal)}
          title={`${bucket} · ${dim === 'bundle' ? 'zone' : 'subcat'} = ${dimVal}`}
          onSession={drillSession} />
      )}
      {view === 'session' && sessionId && (
        <SessionDetail s={sessions.find((x) => x.sessionId === sessionId)
          || payload.sessions.find((x) => x.sessionId === sessionId)!} />
      )}
    </div>
  );
}

// @section 4 Overview — insights + allocation chart + trend
function Overview({ payload, allocation, trend, onBucket, user }: {
  payload: TvPayload; allocation: any[]; trend: any[];
  onBucket: (b: string) => void; user: string;
}) {
  const buckets = [...new Set(trend.map((t) => t.bucket))];
  const days = [...new Set(trend.map((t) => t.day))].sort();
  const trendData = days.map((day) => {
    const row: any = { day: day.slice(5) };
    for (const b of buckets) row[b] = trend.find((t) => t.day === day && t.bucket === b)?.directMinutes || 0;
    return row;
  });

  return (
    <div className="space-y-5">
      {payload.insights.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {payload.insights.map((i) => <InsightCard key={i.id} ins={i} user={user} />)}
        </div>
      )}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        <div className="rounded-xl border border-ink-800 bg-ink-900/60 p-4">
          <div className="text-[10px] uppercase tracking-[2px] text-muted font-semibold mb-3">
            Allocation: actual vs target (% of direct time) — click a bar
          </div>
          <ResponsiveContainer width="100%" height={320}>
            <BarChart data={allocation} margin={{ top: 4, right: 8, left: -16, bottom: 50 }}>
              <CartesianGrid stroke="#1d2a3d" vertical={false} />
              <XAxis dataKey="bucket" tick={{ fill: '#6b7a90', fontSize: 10 }}
                angle={-35} textAnchor="end" interval={0} />
              <YAxis tick={{ fill: '#6b7a90', fontSize: 11 }} unit="%" />
              <Tooltip contentStyle={{ background: '#101828', border: '1px solid #344054', fontSize: 12 }}
                formatter={(v: any, n: any) => [`${v}%`, n]}
                labelStyle={{ color: '#eef1f6' }} />
              <Legend wrapperStyle={{ fontSize: 11, color: '#6b7a90' }} />
              <Bar dataKey="targetPct" name="Target" fill="#344054" radius={[2, 2, 0, 0]} />
              <Bar dataKey="actualPct" name="Actual" radius={[2, 2, 0, 0]}
                cursor="pointer"
                onClick={(d: any) => d && onBucket(d.bucket)}
                fill="#14b8a6"
                shape={(props: any) => {
                  const c = CF_COLOR[props.payload.cashFlowType] || '#14b8a6';
                  return <rect x={props.x} y={props.y} width={props.width} height={props.height}
                    fill={c} rx={2} style={{ cursor: 'pointer' }} />;
                }} />
            </BarChart>
          </ResponsiveContainer>
        </div>
        <div className="rounded-xl border border-ink-800 bg-ink-900/60 p-4">
          <div className="text-[10px] uppercase tracking-[2px] text-muted font-semibold mb-3">
            Daily direct-minute trend by bucket (28d)
          </div>
          <ResponsiveContainer width="100%" height={320}>
            <LineChart data={trendData} margin={{ top: 4, right: 8, left: -16, bottom: 4 }}>
              <CartesianGrid stroke="#1d2a3d" vertical={false} />
              <XAxis dataKey="day" tick={{ fill: '#6b7a90', fontSize: 10 }} interval={3} />
              <YAxis tick={{ fill: '#6b7a90', fontSize: 11 }} unit="m" />
              <Tooltip contentStyle={{ background: '#101828', border: '1px solid #344054', fontSize: 11 }}
                labelStyle={{ color: '#eef1f6' }} />
              {buckets.map((b, i) => (
                <Line key={b} type="monotone" dataKey={b} stroke={LINE_PAL[i % LINE_PAL.length]}
                  dot={false} strokeWidth={1.5} />
              ))}
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>
      <BucketRows allocation={allocation} onBucket={onBucket} />
    </div>
  );
}

// Fallback / always-visible clickable bucket table (drilling never depends on chart click alone)
function BucketRows({ allocation, onBucket }: { allocation: any[]; onBucket: (b: string) => void }) {
  return (
    <div className="rounded-xl border border-ink-800 bg-ink-900/60 p-4">
      <div className="text-[10px] uppercase tracking-[2px] text-muted font-semibold mb-2">All buckets</div>
      <table className="w-full text-xs">
        <thead><tr className="text-muted text-left">
          <th className="py-1.5">Bucket</th><th>Cash-flow</th><th className="text-right">Target</th>
          <th className="text-right">Actual</th><th className="text-right">Δ pp</th>
          <th className="text-right">Direct min</th><th className="text-right">Sessions</th><th className="text-right">Cost</th>
        </tr></thead>
        <tbody>
          {allocation.map((a) => (
            <tr key={a.bucket} className="border-t border-ink-800 cursor-pointer hover:bg-ink-800/40"
              onClick={() => onBucket(a.bucket)}>
              <td className="py-1.5 text-paper-100">{a.bucket}</td>
              <td className="text-muted">{a.cashFlowType}</td>
              <td className="text-right tabular-nums">{a.targetPct ?? '—'}%</td>
              <td className="text-right tabular-nums">{a.actualPct}%</td>
              <td className={`text-right tabular-nums ${a.deltaPp > 3 ? 'text-stall' : a.deltaPp < -3 ? 'text-watch' : 'text-ok'}`}>
                {a.deltaPp > 0 ? '+' : ''}{a.deltaPp}
              </td>
              <td className="text-right tabular-nums">{Math.round(a.directMinutes)}</td>
              <td className="text-right tabular-nums">{a.sessionCount}</td>
              <td className="text-right tabular-nums">${a.costUsd.toFixed(0)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// @section 5 Insight card + copy-prompt
function InsightCard({ ins, user }: { ins: TvInsight; user: string }) {
  const copy = () => {
    navigator.clipboard.writeText(ins.promptTemplate.replace(/\{user\}/g, user))
      .then(() => toast('✓ Deep-dive prompt copied — paste into a Claude Code session'))
      .catch(() => toast('Copy failed'));
  };
  return (
    <div className={`rounded-xl border-2 ${SEV_RING[ins.severity]} bg-gradient-to-br from-ink-900 to-ink-900/40 p-4`}>
      <div className={`text-[10px] uppercase tracking-[2px] font-bold mb-1 ${SEV_TX[ins.severity]}`}>{ins.eyebrow}</div>
      <div className="text-base font-semibold text-paper-100 leading-snug">{ins.headline}</div>
      <div className="text-xs text-muted mt-1">{ins.detail}</div>
      <button onClick={copy}
        className="mt-3 bg-ink-800 hover:bg-accent-600 hover:text-ink-950 text-paper-100 text-xs px-3 py-1.5 rounded transition">
        ⧉ Copy Claude deep-dive prompt
      </button>
    </div>
  );
}

// @section 6 Bucket detail — segmentation (subcategory + zone) + concentration
function BucketDetail({ bucket, allocation, sessions, onSubcat }: {
  bucket: string; allocation: any[]; sessions: TvSession[];
  onSubcat: (b: string, d: 'functionalCategory' | 'bundle', v: string) => void;
}) {
  const rows = sessions.filter((s) => s.bucket === bucket);
  const a = allocation.find((x) => x.bucket === bucket) || {};
  const totDirect = rows.reduce((x, s) => x + s.directMinutes, 0);
  const agg = (field: keyof TvSession) => {
    const m: Record<string, { key: string; direct: number; n: number; cs: number }> = {};
    for (const s of rows) {
      const k = String(s[field] || '(none)');
      (m[k] ||= { key: k, direct: 0, n: 0, cs: 0 });
      m[k].direct += s.directMinutes; m[k].n++; m[k].cs += s.confidence;
    }
    return Object.values(m).map((r) => ({
      ...r, pct: totDirect ? 100 * r.direct / totDirect : 0, avgconf: Math.round(r.cs / r.n),
    })).sort((x, y) => y.direct - x.direct);
  };
  const subcats = agg('functionalCategory');
  const zones = agg('bundle');
  const top5 = [...rows].sort((x, y) => y.directMinutes - x.directMinutes).slice(0, 5);
  const conc = totDirect ? Math.round(100 * top5.reduce((x, s) => x + s.directMinutes, 0) / totDirect) : 0;
  const tiers = agg('tier');

  const Kpi = ({ big, lbl, meta }: { big: string; lbl: string; meta: string }) => (
    <div className="rounded-xl border border-ink-800 bg-ink-900/60 p-4">
      <div className="text-3xl font-bold tabular-nums text-paper-50">{big}</div>
      <div className="text-[10px] uppercase tracking-[2px] text-muted font-semibold mt-1">{lbl}</div>
      <div className="text-[11px] text-muted mt-1">{meta}</div>
    </div>
  );
  const Breakdown = ({ title, data, d }: { title: string; data: any[]; d: 'functionalCategory' | 'bundle' }) => (
    <div className="rounded-xl border border-ink-800 bg-ink-900/60 p-4">
      <div className="text-[10px] uppercase tracking-[2px] text-muted font-semibold mb-2">{title} — click a row</div>
      <table className="w-full text-xs">
        <thead><tr className="text-muted text-left"><th className="py-1">Segment</th>
          <th className="text-right">Direct min</th><th className="text-right">% bkt</th>
          <th className="text-right">Sess</th><th className="text-right">Conf</th></tr></thead>
        <tbody>{data.map((r) => (
          <tr key={r.key} className="border-t border-ink-800 cursor-pointer hover:bg-ink-800/40"
            onClick={() => onSubcat(bucket, d, r.key)}>
            <td className="py-1 text-paper-100">{r.key}</td>
            <td className="text-right tabular-nums">{Math.round(r.direct)}</td>
            <td className="text-right tabular-nums">{r.pct.toFixed(1)}%</td>
            <td className="text-right tabular-nums">{r.n}</td>
            <td className="text-right tabular-nums">{r.avgconf}</td>
          </tr>
        ))}</tbody>
      </table>
    </div>
  );

  return (
    <div className="space-y-5">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <Kpi big={`${Math.round(totDirect)}m`} lbl="Direct time"
          meta={`Target ${a.targetPct ?? '—'}% · Actual ${a.actualPct ?? 0}%`} />
        <Kpi big={String(rows.length)} lbl="Sessions" meta={`$${(a.costUsd ?? 0).toFixed(2)} cost`} />
        <Kpi big={`${a.deltaPp > 0 ? '+' : ''}${a.deltaPp ?? 0}pp`} lbl="Delta vs target"
          meta={`${a.cashFlowType ?? ''} bucket`} />
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        <Breakdown title="By subcategory (functional category)" data={subcats} d="functionalCategory" />
        <Breakdown title="By zone (workspace bundle)" data={zones} d="bundle" />
      </div>
      <div className="rounded-xl border border-ink-800 bg-ink-900/60 p-4 text-xs leading-relaxed text-paper-100">
        <span className="text-muted uppercase tracking-[2px] text-[10px] font-semibold">Tier mix</span>{' '}
        {tiers.map((t) => `${t.key} ${Math.round(t.direct)}m (${t.pct.toFixed(0)}%)`).join(' · ')}
        <br />
        <span className="text-muted uppercase tracking-[2px] text-[10px] font-semibold">Concentration</span>{' '}
        top 5 sessions = <span className={conc >= 60 ? 'text-watch' : 'text-ok'}>{conc}%</span> of this bucket's direct time
        {conc >= 60 ? ' — a few sessions dominate' : ' — reasonably distributed'}
      </div>
    </div>
  );
}

// @section 7 Session table (sortable) + detail
function SessionTable({ rows, title, onSession }: {
  rows: TvSession[]; title: string; onSession: (id: string) => void;
}) {
  const [sk, setSk] = useState<keyof TvSession>('directMinutes');
  const [sd, setSd] = useState(-1);
  const sorted = [...rows].sort((a, b) => {
    const av = a[sk] as any, bv = b[sk] as any;
    if (typeof av === 'string') return sd * av.localeCompare(bv);
    return sd * ((av || 0) - (bv || 0));
  });
  const Th = ({ k, label, r }: { k: keyof TvSession; label: string; r?: boolean }) => (
    <th className={`py-1.5 cursor-pointer hover:text-paper-100 ${r ? 'text-right' : ''}`}
      onClick={() => { if (k === sk) setSd(-sd); else { setSk(k); setSd(-1); } }}>{label}</th>
  );
  return (
    <div className="rounded-xl border border-ink-800 bg-ink-900/60 p-4">
      <div className="text-[10px] uppercase tracking-[2px] text-muted font-semibold mb-2">
        {title} — {rows.length} sessions
      </div>
      <table className="w-full text-xs">
        <thead><tr className="text-muted text-left">
          <Th k="firstSeenAt" label="Date" /><Th k="directMinutes" label="Direct" r />
          <Th k="passiveMinutes" label="Passive" r /><Th k="costUsd" label="Cost" r />
          <Th k="turnCount" label="Turns" r /><Th k="confidence" label="Conf" r />
          <Th k="decisionStage" label="Stage" /><th className="py-1.5">Prompt</th>
        </tr></thead>
        <tbody>{sorted.map((s) => (
          <tr key={s.sessionId} className="border-t border-ink-800 cursor-pointer hover:bg-ink-800/40"
            onClick={() => onSession(s.sessionId)}>
            <td className="py-1.5">{s.firstSeenAt.slice(0, 10)}</td>
            <td className="text-right tabular-nums">{Math.round(s.directMinutes)}</td>
            <td className="text-right tabular-nums">{Math.round(s.passiveMinutes)}</td>
            <td className="text-right tabular-nums">${s.costUsd.toFixed(2)}</td>
            <td className="text-right tabular-nums">{s.turnCount}</td>
            <td className="text-right tabular-nums">
              <span className={s.confidence >= 70 ? 'text-ok' : s.confidence >= 40 ? 'text-watch' : 'text-stall'}>
                {s.confidence}</span>
            </td>
            <td>{s.decisionStage}</td>
            <td className="text-muted max-w-md truncate">{s.prompt.slice(0, 120)}</td>
          </tr>
        ))}</tbody>
      </table>
    </div>
  );
}

function SessionDetail({ s }: { s: TvSession }) {
  const Row = ({ k, v }: { k: string; v: any }) => (
    <><div className="text-muted">{k}</div><div className="text-paper-100 tabular-nums break-words">{String(v)}</div></>
  );
  return (
    <div className="space-y-5">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        <div className="rounded-xl border border-ink-800 bg-ink-900/60 p-4">
          <div className="text-[10px] uppercase tracking-[2px] text-muted font-semibold mb-3">Session attributes</div>
          <div className="grid grid-cols-[140px_1fr] gap-y-1.5 gap-x-4 text-xs">
            <Row k="Session ID" v={s.sessionId} /><Row k="Bucket" v={s.bucket} />
            <Row k="Secondary" v={s.bucketSecondary || '—'} /><Row k="Confidence" v={s.confidence} />
            <Row k="Stage" v={s.decisionStage} /><Row k="Bundle" v={s.bundle} />
            <Row k="Tier" v={s.tier} /><Row k="Functional cat" v={s.functionalCategory} />
            <Row k="Project" v={s.project} />
            <Row k="First seen" v={s.firstSeenAt.slice(0, 19).replace('T', ' ')} />
          </div>
          <div className="mt-3 rounded-md border border-ink-700 bg-ink-950 p-3 text-xs text-watch">
            Why this bucket: {s.decisionReason || '(none)'}
          </div>
        </div>
        <div className="rounded-xl border border-ink-800 bg-ink-900/60 p-4">
          <div className="text-[10px] uppercase tracking-[2px] text-muted font-semibold mb-3">Time &amp; work</div>
          <div className="grid grid-cols-[140px_1fr] gap-y-1.5 gap-x-4 text-xs">
            <Row k="Direct minutes" v={`${Math.round(s.directMinutes)} min`} />
            <Row k="Passive minutes" v={`${Math.round(s.passiveMinutes)} min`} />
            <Row k="Prompt-typing" v={`${s.promptTypingMinutes.toFixed(1)} min`} />
            <Row k="Turns" v={s.turnCount} /><Row k="Cost" v={`$${s.costUsd.toFixed(2)}`} />
            <Row k="ai_finish cov" v={s.aiFinishCoverage == null ? '—' : `${Math.round(s.aiFinishCoverage * 100)}%`} />
            <Row k="Walk-aways" v={s.walkAwayIntervals} />
          </div>
        </div>
      </div>
      <div className="rounded-xl border border-ink-800 bg-ink-900/60 p-4">
        <div className="text-[10px] uppercase tracking-[2px] text-muted font-semibold mb-2">First user prompt</div>
        <pre className="bg-ink-950 border border-ink-700 rounded-md p-3 text-xs text-paper-100 whitespace-pre-wrap break-words max-h-96 overflow-y-auto m-0">{s.prompt || '(no first user message)'}</pre>
      </div>
      <div className="rounded-xl border border-ink-800 bg-ink-900/60 p-4">
        <div className="text-[10px] uppercase tracking-[2px] text-muted font-semibold mb-2">Tool usage</div>
        <div className="bg-ink-950 border border-ink-700 rounded-md p-3 text-xs text-paper-100 break-words">{s.tools || '(no tool calls)'}</div>
      </div>
    </div>
  );
}
